import React, { useState, useEffect, useRef } from 'react';
import { ExternalLink, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewsCarousel = ({ editing = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  
  const scrollContainerRef = useRef(null);
  const pauseTimeoutRef = useRef(null);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/google-rating`);
      if (!response.ok) throw new Error('Failed to fetch reviews');
      
      const result = await response.json();
      
      // Minimum loading time for skeleton
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setData(result);
      setError(null);
    } catch (err) {
      console.error('Error fetching Google reviews:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    let token = sessionStorage.getItem('admin_token');
    
    if (!token) {
      token = prompt('Enter admin refresh token:');
      if (!token) return;
      sessionStorage.setItem('admin_token', token);
    }

    setRefreshing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/refresh-google-rating`, {
        method: 'POST',
        headers: {
          'X-Admin-Token': token
        }
      });

      if (response.status === 401) {
        sessionStorage.removeItem('admin_token');
        alert('Invalid admin token. Please try again.');
        return;
      }

      if (!response.ok) throw new Error('Failed to refresh');

      const result = await response.json();
      setData(result.data);
      alert('✓ Reviews refreshed successfully!');
    } catch (err) {
      alert('✗ Failed to refresh: ' + err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const handlePauseOnTouch = () => {
    setIsPaused(true);
    
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
    
    pauseTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
    }, 4000);
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const cardWidth = 340 + 24; // card width + margin
      const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const toggleExpanded = (index) => {
    setExpandedReviews(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getInitialsColor = (name) => {
    const firstLetter = (name || 'A').charAt(0).toUpperCase();
    const colors = {
      A: 'bg-red-100 text-red-700',
      B: 'bg-blue-100 text-blue-700',
      C: 'bg-green-100 text-green-700',
      D: 'bg-yellow-100 text-yellow-700',
      E: 'bg-purple-100 text-purple-700',
      F: 'bg-pink-100 text-pink-700',
      G: 'bg-indigo-100 text-indigo-700',
      H: 'bg-orange-100 text-orange-700',
    };
    return colors[firstLetter] || 'bg-gray-100 text-gray-700';
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return parts[0][0] + parts[1][0];
    }
    return name.substring(0, 2);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  // Don't render if error or no reviews
  if (error || !loading && (!data || !data.reviews || data.reviews.length === 0)) {
    return null;
  }

  // Loading skeleton
  if (loading) {
    return (
      <div className="w-full py-12 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="h-8 w-48 bg-gray-200 rounded-full animate-pulse" />
          </div>
          <div className="flex gap-6 overflow-hidden">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex-shrink-0 w-[340px] h-64 bg-gray-200 rounded-2xl animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const reviews = data.reviews || [];
  const animationDuration = `${reviews.length * 5}s`;

  return (
    <div
      className="w-full py-12 md:py-16 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden"
      data-testid="reviews-marquee"
      role="region"
      aria-live="off"
    >
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-center gap-4 mb-8 flex-wrap">
          <a
            href={data.google_maps_uri}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-600 to-blue-600 text-white rounded-full font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105"
            data-testid="reviews-rating-pill"
          >
            <span className="text-2xl">★</span>
            <span className="text-lg">{data.rating.toFixed(1)} on Google</span>
            <span className="text-sm opacity-90">· {data.user_rating_count} reviews</span>
          </a>
          
          <a
            href={data.google_maps_uri}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-teal-600 transition-colors text-sm"
            data-testid="reviews-see-all"
          >
            See all reviews on Google
            <ExternalLink className="w-4 h-4" />
          </a>

          {editing && (
            <div className="flex items-center gap-3 ml-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-all"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh now'}
              </button>
              <span className="text-xs text-gray-500">
                Updated {new Date(data.fetched_at).toLocaleString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          )}
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Buttons */}
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-all hover:scale-110"
            aria-label="Previous reviews"
            data-testid="reviews-marquee-prev"
          >
            <ChevronLeft className="w-6 h-6 text-gray-700" />
          </button>
          
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-all hover:scale-110"
            aria-label="Next reviews"
            data-testid="reviews-marquee-next"
          >
            <ChevronRight className="w-6 h-6 text-gray-700" />
          </button>

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="overflow-x-auto scrollbar-hide"
            onTouchStart={handlePauseOnTouch}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div
              className="flex"
              style={{
                animation: isPaused ? 'none' : `scroll ${animationDuration} linear infinite`,
              }}
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              {/* Render reviews twice for seamless loop */}
              {[...reviews, ...reviews].map((review, index) => {
                const isExpanded = expandedReviews[index];
                const textTruncated = review.text.length > 180;
                const displayText = !textTruncated || isExpanded 
                  ? review.text 
                  : review.text.substring(0, 180) + '...';

                return (
                  <div
                    key={`review-${index}`}
                    className="flex-shrink-0 w-[340px] mr-6 bg-white rounded-2xl shadow-md border border-gray-100 p-6 transition-all duration-300 hover:shadow-2xl hover:scale-105 hover:border-teal-300 hover:-translate-y-2"
                    data-testid={`review-card-${index}`}
                  >
                    {/* Author Info */}
                    <div className="flex items-start gap-3 mb-4">
                      {review.author_photo ? (
                        <img
                          src={review.author_photo}
                          alt={review.author_name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold text-sm ${getInitialsColor(review.author_name)}`}>
                          {getInitials(review.author_name)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <a
                          href={review.author_url || data.google_maps_uri}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-gray-900 hover:text-teal-600 transition-colors block truncate"
                        >
                          {review.author_name}
                        </a>
                        <p className="text-xs text-gray-500">{review.relative_time}</p>
                      </div>
                    </div>

                    {/* Rating */}
                    <div className="mb-3">
                      {renderStars(review.rating)}
                    </div>

                    {/* Review Text */}
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {displayText}
                    </p>

                    {/* Read More/Less Toggle */}
                    {textTruncated && (
                      <button
                        onClick={() => toggleExpanded(index)}
                        className="text-teal-600 hover:text-teal-700 text-sm font-medium mt-2"
                      >
                        {isExpanded ? 'Show less' : 'Read more'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default ReviewsCarousel;
